/**
 * free-shipping-widget.js  —  Market Metafield Edition
 * ══════════════════════════════════════════════════════════════════════════════
 *
 * No currency conversion. The threshold comes from a market metafield that is
 * already set in the market's local currency. Shopify's cart.total_price is
 * also in the active market currency. Both are the same unit — just compare.
 *
 * What Liquid provides via data-* attributes:
 *   · data-threshold   = threshold in current-market-currency CENTS
 *   · data-symbol      = currency symbol  (e.g. "lei", "$", "€")
 *   · data-is-suffix   = "true" when symbol follows the number ("99 lei")
 *
 * Events
 * ──────
 *   document  'cart:update'
 *     Payload (any shape):
 *       { cart: { total_price: N } }   ← standard Shopify Ajax response
 *       { total_price: N }
 *       { cartTotal: N }
 *     N is in the active market currency cents.
 *
 * Manual API
 * ──────────
 *   const w = document.querySelector('[data-threshold]')._fswInstance;
 *   w.update(cartCents);   // re-render for a known cart total
 *   w.refresh();           // force a fresh /cart.js fetch
 *
 * window.FreeShippingWidgets → Map<HTMLElement, FreeShippingWidget>
 */

(function () {
  'use strict';

  /* ═══════════════════════════════════════════════════════════════════════════
     FORMATTING
  ═══════════════════════════════════════════════════════════════════════════ */

  /**
   * Format a market-currency cent amount into a display string.
   * Always rounds to whole numbers.
   *
   * @param {number}  cents
   * @param {string}  symbol    - e.g. "lei", "$", "€"
   * @param {boolean} isSuffix  - true → "99 lei", false → "$99"
   * @returns {string}
   */
  function formatAmount(cents, symbol, isSuffix) {
    const whole = Math.round(cents / 100);
    return isSuffix ? `${whole} ${symbol}` : `${symbol}${whole}`;
  }

  function clamp(v, lo, hi) { return Math.min(Math.max(v, lo), hi); }

  /* ═══════════════════════════════════════════════════════════════════════════
     TOKEN REPLACEMENT
  ═══════════════════════════════════════════════════════════════════════════ */

  /**
   * Replace [threshold] and [remaining] tokens in a template string.
   *
   * @param {string} template
   * @param {string} thresholdFmt
   * @param {string} remainingFmt
   */
  function applyTokens(template, thresholdFmt, remainingFmt) {
    return template
      .replace(/\[threshold\]/g, thresholdFmt)
      .replace(/\[remaining\]/g,  remainingFmt || '');
  }

  /* ═══════════════════════════════════════════════════════════════════════════
     ACCORDION
  ═══════════════════════════════════════════════════════════════════════════ */

  function initAccordion(btn) {
    if (!btn) return;
    const bodyId = btn.getAttribute('aria-controls');
    const body   = bodyId ? document.getElementById(bodyId) : null;
    if (!body) return;

    btn.addEventListener('click', () => {
      const isOpen = btn.getAttribute('aria-expanded') === 'true';
      btn.setAttribute('aria-expanded', String(!isOpen));
      isOpen ? body.setAttribute('hidden', '') : body.removeAttribute('hidden');
    });
  }

  /* ═══════════════════════════════════════════════════════════════════════════
     CART FETCH
  ═══════════════════════════════════════════════════════════════════════════ */

  /**
   * Fetch /cart.js → total_price in active market currency cents.
   * Returns null on failure.
   *
   * @returns {Promise<number|null>}
   */
  function fetchCartTotal() {
    return fetch('/cart.js', { headers: { 'Content-Type': 'application/json' } })
      .then(r => {
        if (!r.ok) throw new Error('cart.js ' + r.status);
        return r.json();
      })
      .then(cart => (typeof cart.total_price === 'number' ? cart.total_price : null))
      .catch(() => null);
  }

  /* ═══════════════════════════════════════════════════════════════════════════
     WIDGET CLASS
  ═══════════════════════════════════════════════════════════════════════════ */

  class FreeShippingWidget {
    /**
     * @param {HTMLElement} el  - Widget root element
     */
    constructor(el) {
      if (!el) return;
      this.el = el;

      // Threshold in market-currency cents — from market metafield via Liquid.
      // Same currency as cart.total_price. No conversion needed.
      this.thresholdCents = parseInt(el.dataset.threshold, 10) || 0;

      // Display symbol + position — from Liquid localization
      this.symbol   = el.dataset.symbol   || '';
      this.isSuffix = el.dataset.isSuffix === 'true';

      // Panel elements
      this.panels = {
        free:     el.querySelector('[data-panel="free"]'),
        progress: el.querySelector('[data-panel="progress"]'),
        unlocked: el.querySelector('[data-panel="unlocked"]'),
      };

      // Progress bar
      this.barFill = el.querySelector('.fsw__bar-fill');
      this.barWrap = el.querySelector('[role="progressbar"]');

      // All elements that carry data-template for token replacement
      this.tokenEls = Array.from(el.querySelectorAll('[data-template]'));

      // Last known cart total in market currency cents
      this._lastCartCents = null;

      // Hide until first fetch resolves
      el.classList.add('fsw--loading');

      // Wire accordions on all three panels
      Object.values(this.panels).forEach(panel => {
        if (panel) initAccordion(panel.querySelector('.fsw__toggle'));
      });

      // Listen for cart changes
      window.addEventListener('cartUpdateTrigger',     e => {
        //console.log('cart:update listen')
        this._onCartUpdate(e);
      });
      window.addEventListener('addToCartUpdate', e => {
        //console.log('addToCartUpdate listen');
        this._onCartUpdate(e)
      });

      // Initial render
      this.refresh();
    }

    /* ── Private ───────────────────────────────────────────────────────────── */

    _getState(cartCents) {
      if (this.thresholdCents === 0)    return 'free';
      if (cartCents >= this.thresholdCents) return 'unlocked';
      return 'progress';
    }

    _extractTotal(event) {
      const d = event.detail;
      if (!d) return null;
      if (d.cart  && typeof d.cart.total_price === 'number') return d.cart.total_price;
      if (typeof d.total_price === 'number')                  return d.total_price;
      if (typeof d.cartTotal   === 'number')                  return d.cartTotal;
      return null;
    }

    _onCartUpdate(e) {
      const total = this._extractTotal(e);
      if (total !== null) {
        this.update(total);
      } else {
        this.refresh();
      }
    }

    _renderTokens(cartCents) {
      const threshFmt  = formatAmount(this.thresholdCents, this.symbol, this.isSuffix);
      const remainCents = Math.max(this.thresholdCents - cartCents, 0);
      const remainFmt  = formatAmount(remainCents, this.symbol, this.isSuffix);

      this.tokenEls.forEach(el => {
        const tpl = el.dataset.template || '';
        el.textContent = applyTokens(tpl, threshFmt, remainFmt);
      });
    }

    /* ── Public API ────────────────────────────────────────────────────────── */

    /**
     * Update the widget for a new cart total.
     * @param {number} cartCents  Cart total in market currency cents
     */
    update(cartCents) {
      this._lastCartCents = cartCents;

      const state     = this._getState(cartCents);
      const prevState = this.el.dataset.state;

      // Switch visible panel
      Object.entries(this.panels).forEach(([key, panel]) => {
        if (!panel) return;
        panel.classList.toggle('fsw__panel--hidden', key !== state);
      });
      this.el.dataset.state = state;

      // Replace all text tokens
      this._renderTokens(cartCents);

      // Progress bar
      if (state === 'progress' && this.thresholdCents > 0) {
        const pct = clamp(
          Math.round((cartCents / this.thresholdCents) * 100),
          3,
          100
        );
        if (this.barFill) this.barFill.style.width = pct + '%';
        if (this.barWrap) this.barWrap.setAttribute('aria-valuenow', String(pct));
      }

      // Analytics event
      if (state !== prevState) {
        this.el.dispatchEvent(new CustomEvent('fsw:statechange', {
          bubbles: true,
          detail: { state, cartCents },
        }));
      }
    }

    /**
     * Fetch fresh cart total from /cart.js and re-render.
     * @returns {Promise<void>}
     */
    refresh() {
      return fetchCartTotal().then(total => {
        if (total !== null) {
          this.update(total);
        } else if (this._lastCartCents !== null) {
          this.update(this._lastCartCents);
        } else {
          this._renderTokens(0);
        }

        this.el.classList.remove('fsw--loading');
        this.el.classList.add('fsw--loaded');
      });
    }
  }

  /* ═══════════════════════════════════════════════════════════════════════════
     BOOTSTRAP
  ═══════════════════════════════════════════════════════════════════════════ */

  const registry = new Map();

  function initAll() {
    document.querySelectorAll('[data-threshold]').forEach(el => {
      if (registry.has(el)) return;
      const instance  = new FreeShippingWidget(el);
      el._fswInstance = instance;
      registry.set(el, instance);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:block:select', initAll);
  document.addEventListener('shopify:section:load',  initAll);

  if (typeof window !== 'undefined') {
    window.FreeShippingWidget  = FreeShippingWidget;
    window.FreeShippingWidgets = registry;
  }
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = FreeShippingWidget;
  }

})();